// Notes editor module (SC-N1 extraction, SC-N2 redesigned sheet).
// The closed LIST renders in #root (renderList); the OPEN NOTE is hosted in a body-level,
// class-less-of-`.overlay` sheet overlay that SURVIVES index.html re() (re() only strips
// `body > .overlay`, and this overlay uses `.vn2-overlay`, never `.overlay`).
//
// Lifecycle:
//   renderNotes()  — returns the LIST section; flags that the surface rendered this re() cycle.
//   onRender()     — called from index.html re()/reNow() after the #root swap: sync/teardown the sheet.
//   refresh()      — called from _debouncedRefresh('vault'): never redraws the open note (F-53); catches it up in place.
import { renderList } from './list.js';
import { renderEditorBody, resetNotesPeeks, notesFlush, notesDeviceCopyConfirmed, notesPendingIds, notesTombFilter, notesTombFlush, notesUnsentCount, noteHasEdits, resetNotesAutosave, notesLeaveState, notesDiscard, notesTakeLeaveOffer, notesOnRealtime, notesCatchUp, notesCheckGone, notesDropDeleted, notesRecover, notesMoveTo, notesCreateLocal, resetRichField } from './editor.js';
import { isDefaultNoteTitle, noteDateLabel } from './helpers.js';
import { closeSessionMenu, sessionMenuOpen, autoPickSession, aptNoteState, noteForAppointment } from './picker.js'; // SC5 L3 / L4
import { injectNotesStyles } from './styles.js';
import { attachKbFit } from './kbfit.js';

const H = () => window.HUD || {};

let _ov = null;        // overlay element (document.body child, class vn2-overlay — NOT .overlay)
let _ovSheet = null;   // the sheet (column: header/body); editor root mounts inside
let _kbDetach = null;  // F-63b: removes the keyboard-fit listeners of the CURRENT overlay (set in _mount, run in _teardown)
let _ovNoteId = null;  // note id the sheet currently shows
let _ovAiSt = null;    // SC6a R7: the AI entitlement the open sheet was rendered with (F-53: 'ok' | 'none' | 'pending')
let _renderedThisCycle = false;

function _bpClass(){
  const w = window.innerWidth;
  if(w < 768) return 'vn2-phone';
  if(w < 1024) return 'vn2-tablet';
  return ''; // desktop
}

function _mount(){
  injectNotesStyles();
  const ov = document.createElement('div');
  // z-index 450 (via .vn2-overlay): above #sidenav/#bottom-nav, below openModal/openConfirm (.overlay z 500)
  ov.className = 'vn2-overlay ' + _bpClass();
  const sheet = document.createElement('div');
  sheet.className = 'vn2-sheet';
  sheet.setAttribute('data-notes-overlay-card','1'); // stable hook for verification/tests
  // Backdrop click = same close path as Back (unsaved confirm). Clicks inside the sheet don't close.
  // F-63: the overlay is now the scroller, so a press on its own scrollbar (desktop) also targets it: only the visible area counts as backdrop.
  ov.addEventListener('mousedown', (e)=>{
    if(e.target !== ov) return;
    const r = ov.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
    if(x < ov.clientLeft || x >= ov.clientLeft + ov.clientWidth || y < ov.clientTop || y >= ov.clientTop + ov.clientHeight) return; // on the scrollbar
    _requestClose();
  });
  ov.appendChild(sheet);
  document.body.appendChild(ov);
  _ov = ov; _ovSheet = sheet;
  _kbDetach = attachKbFit(ov, window); // F-63b: size the overlay to the visible viewport while the keyboard is up
  document.addEventListener('keydown', _onKeydown); // N5 sheet shortcuts (removed on teardown)
}

function _teardown(){
  document.removeEventListener('keydown', _onKeydown);
  if(_kbDetach){ _kbDetach(); _kbDetach = null; } // F-63b
  closeSessionMenu(); // SC5 L3
  _endOverlay();      // SC5 L4: opened over the calendar → drop its context + its history entry
  if(_ov){ _ov.remove(); }
  _ov = null; _ovSheet = null; _ovNoteId = null;
  resetNotesPeeks(); // N2b: peeks + in-memory content cache clear on sheet close/teardown
  resetNotesAutosave(); // Notes SC2: any change still pending is saved in the background, then the engine stops
  resetRichField();     // SC6a: the editor goes; it keeps its last text for that background save
}

// N5 — sheet keyboard shortcuts (active only while the sheet is mounted).
//   Esc = Back (via the unsaved confirm); with the floating window focused, Esc closes it first.
//   Cmd/Ctrl+S = save now (Notes SC2: flushes the autosave; there is no Save button), suppressing the browser dialog.
// Ignored entirely while a modal/confirm (body > .overlay, z 500) is open — that owns its keys.
function _onKeydown(e){
  if(!_ovSheet) return;
  if(document.querySelector('body > .overlay')) return; // a modal/confirm is open
  if(e.key === 'Escape'){
    if(sessionMenuOpen()){ closeSessionMenu(); e.preventDefault(); return; } // SC5 L3: Esc closes the session menu first
    const win = _ovSheet.querySelector('.vn2-peek-win.vn2-peek-open');
    if(win && win.contains(document.activeElement)){
      const closeBtn = win.querySelector('.vn2-peek-close');
      if(closeBtn) closeBtn.click();
      e.preventDefault();
      return;
    }
    e.preventDefault();
    _requestClose();
    return;
  }
  if((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')){
    e.preventDefault(); // suppress the browser Save dialog whenever the sheet is open
    notesFlush();
  }
}

function _renderBody(){
  const S = H().S;
  const id = S.activeVaultId;
  if(!id || !_ov) return;
  const note = (H().DB.vaultNotes || []).find(n => n.id === id);
  if(!note){ _close(); return; }
  closeSessionMenu(); // SC5 L3: the menu belongs to the editor being replaced
  _ovAiSt = _aiState();
  _ovSheet.replaceChildren(renderEditorBody(note, { onReflect:_reflect, onClose:_close, requestClose:_requestClose,
    onBreakpoint:()=>{ if(_ov) _ov.className = 'vn2-overlay ' + _bpClass(); }, // F-63c: the overlay's tier class follows a resize across 768
    openNote:_switchNote })); // SC5: open another note in the same sheet
}

// After a state-changing write: re-render the sheet in place AND re() the list.
function _reflect(){ _renderBody(); H().re(); }

// Close the sheet: clear state, tear down, re() the list.
// SC4b: the note surface just closed — drain a deferred SW reload if the app is now clean.
function _close(){ H().S.activeVaultId = null; _teardown(); H().re(); try{ H()._maybeSwReload?.(); }catch(_){} }

// Leave the note (Back button + backdrop click + Esc). Notes SC2 (A2):
//   saved → leave at once · saving → wait for it (the pill shows Saving…), then leave ·
//   dirty or failing → flush once; if it still fails: the text confirmed on this device (U-2) → leave with NO dialog
//   (the engine keeps retrying in the background, the device copy stays); otherwise the EXISTING discard confirm
//   (Stay = keep editing; Leave = drop the in-memory edit; a stored device copy is never deleted).
// After a saved leave, the homework suggestion (decision 7) opens over the list.
let _leaving = false;
async function _requestClose(){
  if(_leaving) return;
  const S = H().S;
  const id = S.activeVaultId;
  const st = notesLeaveState();
  // Notes SC3: deleted on another device → close with no prompt; nothing is written (never re-create it).
  if(st && st.conflict==='deleted'){ notesDropDeleted(); _leaveNote(id, false); return; }
  // An unresolved conflict. C3: nothing unsaved in this window (no edit since the conflict appeared or since
  // the last successful save) → close silently; the next open loads the latest version. Unsaved text → the
  // EXISTING discard confirm (Stay keeps the banner; Leave discards my version). Nothing is written either way.
  if(st && st.conflict==='conflict' && !st.dirty){ notesDiscard(); _leaveNote(id, false); return; }
  if(st && st.conflict==='conflict'){
    H().openConfirm(H().T('vault_unsaved_changes'), H().T('vault_minimise_unsaved'), true, ()=>{ notesDiscard(); _leaveNote(id, false); });
    return;
  }
  if(st && (st.dirty || st.saving)){
    _leaving = true;
    let ok = false, kept = false;
    try{
      ok = await notesFlush();
      if(!ok && !(notesLeaveState()||{}).conflict) kept = await notesDeviceCopyConfirmed(); // U-2: kept on the device
    } finally { _leaving = false; }
    if(S.activeVaultId !== id) return; // something else closed/switched the sheet meanwhile
    if(!ok && kept){
      _leaveNote(id, false, { kept:true });
      if(navigator.onLine===false){ try{ H().toast(H().T('notes_st_offline_kept')); }catch(_){} } // the left note: saved on this device
      return;
    }
    if(!ok){
      H().openConfirm(H().T('vault_unsaved_changes'), H().T('vault_minimise_unsaved'), true, ()=>{
        notesDiscard(); _leaveNote(id, false);
      });
      return;
    }
  }
  _leaveNote(id, true);
}
function _leaveNote(id, saved, opts={}){
  const S = H().S;
  const offer = saved ? notesTakeLeaveOffer() : null; // capture before the sheet (and its engine) is torn down
  // U-2: a note left with its text kept on the device keeps its draft maps (reopening it before the background save
  // shows the unsaved text, U-1) and stays listed; the engine clears them once its save lands.
  if(id && !opts.kept){ if(S._vaultDraftContent)delete S._vaultDraftContent[id]; if(S._vaultDraftTitle)delete S._vaultDraftTitle[id]; if(S._vaultTidyRaw)delete S._vaultTidyRaw[id]; }
  S.vaultHasUnsaved=false; S.vaultSummary=null; // SC1c: null = untouched
  // Notes SC2 A3: a new note that was never saved (untouched, or discarded) simply disappears — no write.
  if(id && !opts.kept && (H().DB.vaultNotes||[]).some(n=>n.id===id && n.local)) H().DB.vaultNotes = H().DB.vaultNotes.filter(n=>n.id!==id);
  S.activeVaultId = null; _teardown(); H().re();
  // The offer opens over the list (its modal holds the render lock); with no offer, drain a deferred SW reload.
  let shown = false;
  try{ shown = !!(offer && offer()); }catch(e){ console.error('❌ homework offer:', e); }
  if(!shown){ try{ H()._maybeSwReload?.(); }catch(_){} }
}

// SC5 L3: open another note in the same sheet (the session picker; the "session taken" path). The open note is
// saved first. If that fails, queue item 19 (Q6 A, narrowing decision 23): the switch still goes ahead once the device
// copy of the current edit is CONFIRMED written; the note then keeps retrying in the background (A4) and saves when
// back online. No confirmed device copy, or a conflict with unsaved text → the switch is refused and the note stays
// open. No homework offer (the sheet is not being left). opts.dropIfRefused: the target is a new local note made for
// this switch → removed again when refused.
let _switching = false;
async function _switchNote(nid, opts={}){
  const S = H().S, id = S.activeVaultId;
  if(!nid || nid === id || _switching || _leaving) return false;
  const drop = ()=>{ if(opts.dropIfRefused) H().DB.vaultNotes = (H().DB.vaultNotes||[]).filter(n=>!(n.id===nid && n.local)); };
  const st = notesLeaveState();
  let ok = true, kept = false;
  if(st && st.conflict==='deleted') notesDropDeleted();
  else if(st && st.conflict==='conflict'){ if(st.dirty) ok = false; else notesDiscard(); }
  else if(st && (st.dirty || st.saving)){
    _switching = true;
    try{
      ok = await notesFlush();
      if(!ok && !(notesLeaveState()||{}).conflict) ok = kept = await notesDeviceCopyConfirmed(); // item 19: kept on the device
    } finally { _switching = false; }
    if(S.activeVaultId !== id){ drop(); return false; } // closed / switched meanwhile
  }
  if(!ok){ drop(); try{ H().toast(H().T('notes_err_save'),'error'); }catch(_){} return false; }
  // Kept on the device: the draft maps stay (reopening the note before its background save shows the unsaved text,
  // as for any note left unsaved — the engine clears them once its save lands).
  if(id && !kept){ if(S._vaultDraftContent)delete S._vaultDraftContent[id]; if(S._vaultDraftTitle)delete S._vaultDraftTitle[id]; if(S._vaultTidyRaw)delete S._vaultTidyRaw[id];
    if((H().DB.vaultNotes||[]).some(n=>n.id===id && n.local)) H().DB.vaultNotes = H().DB.vaultNotes.filter(n=>n.id!==id); } // an untouched new note just disappears
  if(kept && navigator.onLine===false){ try{ H().toast(H().T('notes_st_offline_kept')); }catch(_){} } // the left note: saved on this device
  S.vaultHasUnsaved = false; S.vaultSummary = null;
  S.activeVaultId = nid; H().re();
  return true;
}

// ── SC5 L4 (decision 20 / 22): open a session's note OVER the current screen (calendar / Today) ──
// S.activeTab is never changed; the sheet carries its own patient context (S._notesCtx, read by notePatientCtx) and
// survives re() while that context is set. A history entry is recorded BEFORE opening: the current entry becomes the
// screen as it is now, the new one is the open note → the back gesture closes the sheet and restores that screen.
// Every close (Back / ✕ / Esc / backdrop / discard → Leave / after the homework offer) goes through _teardown, which
// clears the context and consumes that history entry (history.back → popstate restores the same screen).
let _ovHist = false;
function openForAppointment(apt){
  const h = H(), S = h.S, DB = h.DB;
  if(!aptNoteState(apt)) return false;
  const ctx = apt.childId
    ? { kind:'child', child_id:apt.childId, household_id:((DB.children||[]).find(c=>c.id===apt.childId)||{}).householdId||null }
    : { kind:'spec_patient', specialist_patient_id:apt.specialistPatientId };
  try{ const snap = h._snapNav(); window.history.replaceState(snap, ''); window.history.pushState(Object.assign({}, snap, { _notesOv:1 }), ''); _ovHist = true; }
  catch(e){ console.error('❌ notes history entry:', e); }
  S._notesCtx = ctx;
  let n = noteForAppointment(apt.id);
  if(!n) n = notesCreateLocal({ childId:apt.childId||null, specialistPatientId:apt.childId?null:(apt.specialistPatientId||null),
    appointment:{ id:apt.id, date:apt.date, time:apt.time, title:apt.title||'' } }); // no row until it has text
  S.activeVaultId = n.id; S.vaultSummary = null; S.vaultHasUnsaved = false;
  _syncOpen(); // mount over the current screen — no re(), so the calendar underneath keeps its date / view / scroll
  return true;
}
function _endOverlay(){
  const S = H().S;
  if(S && S._notesCtx) S._notesCtx = null;
  if(_ovHist){ _ovHist = false;
    try{ if(window.history.state && window.history.state._notesOv) window.history.back(); }catch(e){ console.error('❌ notes history back:', e); } } // already popped (back gesture) → nothing
}
// The sheet was closed from outside (the back gesture restored a screen with no open note): an untouched new
// note disappears (no row); a note with unsaved text is saved in the background by the engine (A4), as before.
function _dropUntouched(id){
  const S = H().S, st = notesLeaveState();
  if(!id || (st && (st.dirty || st.saving))) return;
  if(S._vaultDraftContent) delete S._vaultDraftContent[id]; if(S._vaultDraftTitle) delete S._vaultDraftTitle[id];
  if((H().DB.vaultNotes||[]).some(n=>n.id===id && n.local)) H().DB.vaultNotes = H().DB.vaultNotes.filter(n=>n.id!==id);
}

// F-53: this window has unsaved text for the note (typed in this open, or a title / content draft).
function _unsavedHere(id){ const S = H().S; return !!((S._vaultDraftContent && S._vaultDraftContent[id] != null) || (S._vaultDraftTitle && S._vaultDraftTitle[id] != null) || noteHasEdits(id)); }

// Ensure the sheet reflects S.activeVaultId. Preserves an already-open same-id sheet (survives re()).
function _syncOpen(){
  const S = H().S;
  const id = S.activeVaultId;
  if(!id){ if(_ov){ _dropUntouched(_ovNoteId); _teardown(); } return; }
  const note = (H().DB.vaultNotes || []).find(n => n.id === id);
  // F-53: the open note vanished from the list while this window has unsaved text → keep the sheet; the editor asks the server
  // (one row) and SC3's deleted-elsewhere banner decides (Copy my text). Nothing unsaved → it closes, as before.
  if(!note){ if(_ov && _ovNoteId === id && _unsavedHere(id)){ notesCheckGone(id); return; } if(_ov) _teardown(); S.activeVaultId = null; return; }
  // SC1c: a different note → its summary starts untouched (null), so no stale summary carries across notes
  // (deep links from search/home set activeVaultId without resetting it).
  if(!_ov){ _mount(); _ovNoteId = id; S.vaultSummary = null; _renderBody(); return; }
  // keep the breakpoint class current (cheap; harmless if unchanged)
  _ov.className = 'vn2-overlay ' + _bpClass();
  if(_ovNoteId !== id){ _ovNoteId = id; S.vaultSummary = null; _renderBody(); return; }
  // same id already open → preserve the live editor (do NOT rebuild — survives re()).
}

function onRender(){
  if(_renderedThisCycle || (H().S && H().S._notesCtx)){ // SC5 L4: a sheet opened over the calendar survives re()
    _syncOpen();
  } else {
    if(_ov) _teardown();
    if(H().S && H().S.activeVaultId != null) H().S.activeVaultId = null;
  }
  _renderedThisCycle = false;
}

// Called from _debouncedRefresh (the vault surface, or an unconditional refresh). F-53: it NEVER redraws the open note — a
// redraw re-attaches the live editor, which drops focus and the iPad caret, and it didn't take a newer version anyway.
//   the note is gone from the list → close it, unless this window has unsaved text (SC3's deleted-elsewhere banner decides);
//   otherwise → a newer version of THIS note is caught up in place (C4: applied when idle, the banner with unsaved edits),
//   and the past-sessions list is repainted in place. Updates about other notes leave the editor untouched.
function refresh(){
  const S = H().S;
  const id = S && S.activeVaultId;
  if(!_ov || !id) return;
  const note = (H().DB.vaultNotes || []).find(n => n.id === id);
  if(!note){
    if(_unsavedHere(id)){ notesCheckGone(id); return; } // never close over unsaved text: SC3's banner decides
    _close(); return;
  }
  notesCatchUp(id, note.lastSaved);
}

function renderNotes(){ injectNotesStyles(); _renderedThisCycle = true; return renderList(); } // inject here too: the list (#root) can render before any sheet _mount()

// SC6a R7 (QA-25): the AI entitlement changed (a trial started from inside the editor, or the entitlement check resolved
// after the sheet opened) → re-render the open sheet ONLY when access actually changed. The rich editor instance (if any)
// survives the re-render (same note); a free → trial change mounts it and loads the note; nothing typed is lost (the
// title and summary drafts are in S, and the free tier has no clinical editing).
// F-53 F53-2: a 'pending' sheet (Loading…, not editable) repaints when the check returns → editable by itself (nothing could be
// typed, so nothing is lost). A sheet is never switched TO 'pending': a note already editable stays editable.
function _aiState(){ try{ const h=H(); return h._specAiState ? h._specAiState() : (h._hasSpecAiAccess() ? 'ok' : 'none'); }catch(_){ return null; } }
function onEntitlementChange(){
  const S = H().S;
  if(!_ov || !S || !S.activeVaultId) return false;
  const now = _aiState();
  if(now == null || now === 'pending' || now === _ovAiSt) return false;
  _renderBody();
  return true;
}

function open(id){ if(id != null) H().S.activeVaultId = id; H().re(); }
function close(){ _close(); }

export function initNotes(){
  // eslint-disable-next-line no-console
  console.log('[Huddledin] Notes module loaded (SC-N2)');
}

window.HUD_NOTES = { renderNotes, onRender, refresh, open, close, onRealtime: notesOnRealtime, recover: notesRecover, pendingIds: notesPendingIds, tombFilter: notesTombFilter, flushTombstones: notesTombFlush, unsentCount: notesUnsentCount, // F-10 · F-15 / QS1 // SC3 / SC4
  moveTo: notesMoveTo, createLocal: notesCreateLocal, autoPick: autoPickSession,       // SC5: the session link
  aptNoteState, openForAppointment,                                                     // SC5 L4: the appointment popup
  onEntitlementChange,                                                                  // SC6a R7: QA-25
  isDefaultTitle: isDefaultNoteTitle, dateLabel: noteDateLabel };                       // SC5 L2: parent view (queue item 5)
